import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    useTheme,
    useMediaQuery,
    Snackbar,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlightIcon from '@mui/icons-material/Flight';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { SidebarAd, InFeedAd } from '../components/ads';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

interface Destination {
    id: number;
    name: string;
    country: string;
    image: string;
    description: string;
    highlights: string[];
    bestTime: string;
    budget: string;
    flightTime: string;
}

const destinations: Destination[] = [
    {
        id: 1,
        name: 'Santorini',
        country: 'Greece',
        image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800',
        description: 'Famous for its stunning sunsets, white-washed buildings, and crystal-clear waters. A romantic paradise in the Aegean Sea.',
        highlights: ['Oia Sunset', 'Blue Domed Churches', 'Wine Tasting', 'Volcanic Beaches'],
        bestTime: 'April - October',
        budget: '$150-300/day',
        flightTime: '10-12 hours',
    },
    {
        id: 2,
        name: 'Kyoto',
        country: 'Japan',
        image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800',
        description: 'Ancient temples, traditional gardens, and the heart of Japanese culture. Experience the beauty of old Japan.',
        highlights: ['Fushimi Inari Shrine', 'Bamboo Grove', 'Geisha District', 'Tea Ceremonies'],
        bestTime: 'March - May, Oct - Nov',
        budget: '$100-250/day',
        flightTime: '13-15 hours',
    },
    {
        id: 3,
        name: 'Machu Picchu',
        country: 'Peru',
        image: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=800',
        description: 'The legendary lost city of the Incas. A bucket-list destination set high in the Andes Mountains.',
        highlights: ['Inca Trail', 'Sun Gate', 'Sacred Valley', 'Cusco City'],
        bestTime: 'May - September',
        budget: '$80-200/day',
        flightTime: '9-12 hours',
    },
    {
        id: 4,
        name: 'Bali',
        country: 'Indonesia',
        image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800',
        description: 'Island of the Gods with lush rice terraces, ancient temples, and world-class beaches.',
        highlights: ['Ubud Rice Terraces', 'Tanah Lot Temple', 'Beach Clubs', 'Yoga Retreats'],
        bestTime: 'April - October',
        budget: '$50-150/day',
        flightTime: '18-22 hours',
    },
    {
        id: 5,
        name: 'Cape Town',
        country: 'South Africa',
        image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800',
        description: 'Where mountains meet the ocean. Stunning natural beauty, wine country, and vibrant culture.',
        highlights: ['Table Mountain', 'Cape Peninsula', 'Wine Tasting', 'Safari Day Trips'],
        bestTime: 'November - March',
        budget: '$80-180/day',
        flightTime: '16-20 hours',
    },
    {
        id: 6,
        name: 'Iceland',
        country: 'Iceland',
        image: 'https://images.unsplash.com/photo-1529963183134-61a90db47eaf?w=800',
        description: 'Land of fire and ice. Witness northern lights, geysers, waterfalls, and otherworldly landscapes.',
        highlights: ['Northern Lights', 'Blue Lagoon', 'Golden Circle', 'Glacier Hiking'],
        bestTime: 'Sept - March (lights), June - Aug (hiking)',
        budget: '$200-400/day',
        flightTime: '6-8 hours',
    },
];

const DestinationsPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
    const [comingSoonMessage, setComingSoonMessage] = useState('');
    const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
    const bookingEnabled = !flagsLoading && isEnabled('booking_enabled') === true;

    const handleCardClick = (destination: Destination) => {
        setSelectedDestination(destination);
    };

    const handleClose = () => {
        setSelectedDestination(null);
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <Container maxWidth="lg" sx={{ pt: 4, pb: 6 }}>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        textAlign: 'center',
                    }}
                >
                    Popular Destinations
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.secondary',
                        mb: 6,
                        textAlign: 'center',
                    }}
                >
                    Curated travel packages to amazing places
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                        <Grid container spacing={4}>
                            {destinations.map((destination, index) => (
                                <React.Fragment key={destination.id}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Card
                                            onClick={() => handleCardClick(destination)}
                                            sx={{
                                                height: '100%',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                                                },
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={destination.image}
                                                alt={destination.name}
                                            />
                                            <CardContent>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                    {destination.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {destination.country}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip label={destination.budget} size="small" icon={<AttachMoneyIcon />} />
                                                    <Chip label={destination.flightTime} size="small" icon={<FlightIcon />} />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Mobile In-Feed Ad: After every 4 cards */}
                                    {isMobile && (index + 1) % 4 === 0 && index !== destinations.length - 1 && (
                                        <Grid item xs={12}>
                                            <InFeedAd page="destinations" index={Math.floor(index / 4)} variant="standard" />
                                        </Grid>
                                    )}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Sidebar Ad - Desktop Only */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <SidebarAd page="destinations" />
                        </Grid>
                    )}
                </Grid>
            </Container>

            <Dialog
                open={!!selectedDestination}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                {selectedDestination && (
                    <>
                        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedDestination.name}, {selectedDestination.country}
                            </Typography>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box
                                component="img"
                                src={selectedDestination.image}
                                alt={selectedDestination.name}
                                sx={{
                                    width: '100%',
                                    height: '300px',
                                    objectFit: 'cover',
                                    borderRadius: 2,
                                    mb: 3,
                                }}
                            />
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                {selectedDestination.description}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WbSunnyIcon color="primary" />
                                    <Typography variant="body2">
                                        <strong>Best Time:</strong> {selectedDestination.bestTime}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AttachMoneyIcon color="primary" />
                                    <Typography variant="body2">
                                        <strong>Budget:</strong> {selectedDestination.budget}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FlightIcon color="primary" />
                                    <Typography variant="body2">
                                        <strong>Flight:</strong> {selectedDestination.flightTime}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Highlights
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {selectedDestination.highlights.map((highlight) => (
                                    <Chip key={highlight} label={highlight} color="primary" variant="outlined" />
                                ))}
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    if (!bookingEnabled) {
                                        setComingSoonMessage('Booking is coming soon - stay tuned!');
                                        return;
                                    }
                                    handleClose();
                                    navigate(`/book?from=destination&destination=${encodeURIComponent(selectedDestination?.name || "")}`);
                                }}
                            >
                                Book This Package
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            {/* Coming Soon Snackbar */}
            <Snackbar
                open={!!comingSoonMessage}
                autoHideDuration={4000}
                onClose={() => setComingSoonMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setComingSoonMessage('')}
                    severity="info"
                    sx={{ width: '100%' }}
                >
                    {comingSoonMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DestinationsPage;
